import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Button, Select, Space, message, Spin, Card } from 'antd';
import { useDispatch } from 'react-redux';
import { setBreadcrumbList } from '../../../store/menuInfo/menuInfo';
import apis from '../../../apis';
import './index.scss';

const { Option } = Select;
const { TextArea } = Input;

const PlanEdit = ({ id, type, onBack }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [detail, setDetail] = useState({});
    const [allVersions, setAllVersions] = useState([]);
    const [currentVersionId, setCurrentVersionId] = useState(null);
    const [initialValues, setInitialValues] = useState({});
    const [isFormChanged, setIsFormChanged] = useState(false);
    const dispatch = useDispatch();

    const isEdit = !!id;
    const isView = type === 'view';

    useEffect(() => {
        let title = '新增方案';
        if (isView) title = '查看方案';
        else if (isEdit) title = '编辑方案';

        dispatch(setBreadcrumbList([
            { title: '充值方案' },
            { title: 'TOB方案管理', onClick: onBack },
            { title }
        ]));
    }, [dispatch, isEdit, isView, onBack]);

    useEffect(() => {
        if (isEdit || isView) {
            setLoading(true);
            apis.getTopupPlanDetail({ topup_plan_id: id }).then(res => {
                if (res.code === 200) {
                    const data = res.data || [];
                    if (Array.isArray(data) && data.length > 0) {
                        setAllVersions(data);
                        // Default to the first version (latest) or the one matching current logic if needed
                        // The requirement says "default select version shown in list", but list shows plan info.
                        // Assuming the first item in array is the one we want to show initially (usually latest)
                        // However, if we need to match specific version logic, we might need more info.
                        // For now, let's select the first one.
                        const currentVer = data[0];
                        setDetail(currentVer);
                        setInitialValues(currentVer);
                        setIsFormChanged(false);
                        setCurrentVersionId(currentVer.topup_plan_ver_id);
                        form.setFieldsValue(currentVer);
                    } else {
                         // Fallback for single object response if API changes or empty array
                         const singleData = Array.isArray(data) ? {} : data;
                         setDetail(singleData);
                         setInitialValues(singleData);
                         setIsFormChanged(false);
                         form.setFieldsValue(singleData);
                    }
                } else {
                    message.error(res.message || '获取详情失败');
                }
            }).catch(() => {
                message.error('获取详情失败');
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [id, form, isEdit, isView]);

    const handleValuesChange = (changedValues, allValues) => {
        if (!isEdit) return;
        const fieldsToCheck = ['topup_plan_name', 'equity_point', 'original_price', 'discount_price', 'topup_plan_desc'];
        console.log(allValues,initialValues)
        const hasChange = fieldsToCheck.some(key => {
            const initVal = initialValues[key];
            const currVal = allValues[key];
            if (key === 'topup_plan_name' || key === 'topup_plan_desc') {
                 return (initVal || '') !== (currVal || '');
            }
            return Number(initVal) !== Number(currVal);
        });
        setIsFormChanged(hasChange);
    };

    const handleVersionChange = (verId) => {
        const selectedVer = allVersions.find(v => v.topup_plan_ver_id === verId);
        if (selectedVer) {
            setDetail(selectedVer);
            setCurrentVersionId(verId);
            form.setFieldsValue(selectedVer);
        }
    };

    const handleSave = () => {
        form.validateFields().then(values => {
            setSubmitting(true);
            
            if (isEdit) {
                const { topup_plan_code, topup_plan_type_code, ...rest } = values;
                apis.updateTopupPlan({ ...rest, topup_plan_id: id }).then(res => {
                    if (res.code === 200) {
                        message.success('保存成功');
                        onBack();
                    } else {
                        message.error(res.message || '保存失败');
                    }
                }).catch(() => {
                    message.error('保存失败');
                }).finally(() => {
                    setSubmitting(false);
                });
            } else {
                apis.addTopupPlan(values).then(res => {
                    if (res.code === 200) {
                        message.success('保存成功');
                        onBack();
                    } else {
                        message.error(res.message || '保存失败');
                    }
                }).catch(() => {
                    message.error('保存失败');
                }).finally(() => {
                    setSubmitting(false);
                });
            }
        });
    };

    const getPlanTypeText = (code) => {
        return code === 'ENTERPRISE_EDITION' ? '企业版' : (code === 'PERSONAL_EDITION' ? '个人版' : code);
    };

    return (
        <div className={`plan-edit-container ${isView ? 'view-mode' : ''}`}>
            <Spin spinning={loading}>
                <div className="content-body">
                    <Card bordered={false}>
                        {isView && allVersions.length > 0 && (
                            <div style={{ position: 'absolute', right: 24, top: 24, zIndex: 1 }}>
                                <Select
                                    value={currentVersionId}
                                    onChange={handleVersionChange}
                                    style={{ width: 160 }}
                                    placeholder="选择版本"
                                >
                                    {allVersions.map(ver => (
                                        <Option key={ver.topup_plan_ver_id} value={ver.topup_plan_ver_id}>
                                            {ver.ver_no} ({ver.ver_status_code === 'PUBLISHED' ? '已发布' : (ver.ver_status_code === 'REJECTED' ? '已驳回' : (ver.ver_status_code === 'AUDITING' || ver.ver_status_code === 'UNDER_REVIEW' ? '审核中' : '待发布'))})
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        )}
                        <div className="itemTitle2">
                            <span>
                                01<i></i>
                            </span>
                            基本信息
                        </div>

                        <Form
                            form={form}
                            onValuesChange={handleValuesChange}
                            layout="horizontal"
                            initialValues={{
                                topup_plan_type_code: 'ENTERPRISE_EDITION',
                            }}
                            labelCol={{ flex: '90px' }}
                            wrapperCol={{ flex: 'auto' }}
                            labelAlign="left"
                            colon={false}
                        >
                            <div className="subscriptionItemsContent">
                                <Form.Item
                                    name="topup_plan_type_code"
                                    label="方案类别"
                                    rules={[{ required: true, message: '请选择方案类别' }]}
                                >
                                    {isView ? (
                                        <span className="view-text">{getPlanTypeText(detail.topup_plan_type_code)}</span>
                                    ) : (
                                        <Select placeholder="请选择方案类别" disabled={isEdit}>
                                            <Option value="ENTERPRISE_EDITION">企业版</Option>
                                            <Option value="PERSONAL_EDITION">个人版</Option>
                                        </Select>
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="topup_plan_code"
                                    label="方案编码"
                                    rules={[{ required: true, message: '请输入方案编码' }]}
                                >
                                    {isView ? (
                                        <span className="view-text">{detail.topup_plan_code}</span>
                                    ) : (
                                        <Input placeholder="请输入方案编码" disabled={isEdit} />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="topup_plan_name"
                                    label="方案名称"
                                    rules={[{ required: true, message: '请输入方案名称' }]}
                                >
                                    {isView ? (
                                        <span className="view-text">{detail.topup_plan_name}</span>
                                    ) : (
                                        <Input placeholder="请输入方案名称" />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="equity_point"
                                    label="权益点"
                                    rules={[{ required: true, message: '请输入权益点' }]}
                                >
                                    {isView ? (
                                        <span className="view-text">{detail.equity_point}</span>
                                    ) : (
                                        <InputNumber style={{ width: '100%' }} placeholder="请输入权益点" precision={0} min={0} />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="original_price"
                                    label="原价 (元)"
                                    rules={[{ required: true, message: '请输入原价' }]}
                                >
                                    {isView ? (
                                        <span className="view-text">¥{Number(detail.original_price || 0).toFixed(2)}</span>
                                    ) : (
                                        <InputNumber style={{ width: '100%' }} placeholder="请输入原价" precision={2} min={0} />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="discount_price"
                                    label="折扣价 (元)"
                                    dependencies={['original_price']}
                                    rules={[
                                        { required: true, message: '请输入折扣价' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                const originalPrice = getFieldValue('original_price');
                                                if (value === undefined || value === null || originalPrice === undefined || originalPrice === null) {
                                                    return Promise.resolve();
                                                }
                                                if (Number(value) > Number(originalPrice)) {
                                                    return Promise.reject(new Error('折扣价不能大于原价'));
                                                }
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    {isView ? (
                                        <span className="view-text">{detail.discount_price ? `¥${Number(detail.discount_price).toFixed(2)}` : '-'}</span>
                                    ) : (
                                        <InputNumber style={{ width: '100%' }} placeholder="请输入折扣价" precision={2} min={0} />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="topup_plan_desc"
                                    label="方案描述"
                                    className="full-width"
                                    labelCol={{ flex: '90px' }}
                                    wrapperCol={{ flex: 'auto' }}
                                >
                                    {isView ? (
                                        <span className="view-text">{detail.topup_plan_desc || '-'}</span>
                                    ) : (
                                        <TextArea rows={4} placeholder="请输入方案描述" />
                                    )}
                                </Form.Item>
                            </div>
                        </Form>
                    </Card>
                </div>

                <div className="footer-actions">
                    {isView ? (
                         <Button type="primary" onClick={onBack}>关闭</Button>
                    ) : (
                        <>
                            <Button onClick={onBack}>取消</Button>
                            <Button style={{ marginLeft: "24px" }} type="primary" onClick={handleSave} loading={submitting} disabled={isEdit && !isFormChanged}>{isEdit ? '保存' : '新增'}</Button>
                        </>
                    )}
                </div>
            </Spin>
        </div>
    );
};

export default PlanEdit;
